import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Assignment,
  Timeline,
  Warning,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { get, post, put, del } from '../../config/axios';

const LowFodmapPrompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    promptName: '',
    promptTemplate: '',
    description: '',
    applicablePhases: [],
    priority: 1,
    isEnabled: true
  });

  const phases = ['elimination', 'introduction', 'maintenance'];
  const priorities = [1, 2, 3, 4, 5];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await get('/api/dashboard/diet-prompts?applicableDiets=low_fodmap');
      if (response.success) {
        setPrompts(response.data);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        promptName: prompt.promptName,
        promptTemplate: prompt.promptTemplate,
        description: prompt.description,
        applicablePhases: prompt.applicablePhases || [],
        priority: prompt.priority || 1,
        isEnabled: prompt.isEnabled
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        promptName: '',
        promptTemplate: '',
        description: '',
        applicablePhases: [],
        priority: 1,
        isEnabled: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrompt(null);
    setFormData({
      promptName: '',
      promptTemplate: '',
      description: '',
      applicablePhases: [],
      priority: 1,
      isEnabled: true
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingPrompt) {
        await put(`/api/dashboard/diet-prompts/${editingPrompt._id}`, formData);
      } else {
        await post('/api/dashboard/diet-prompts', {
          ...formData,
          applicableDiets: ['low_fodmap']
        });
      }
      fetchPrompts();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt');
    }
  };

  const handleDelete = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      try {
        await del(`/api/dashboard/diet-prompts/${promptId}`);
        fetchPrompts();
      } catch (error) {
        console.error('Error deleting prompt:', error);
        setError('Failed to delete prompt');
      }
    }
  };

  const handleToggleStatus = async (promptId, currentStatus) => {
    try {
              await put(`/api/dashboard/diet-prompts/${promptId}`, {
          isEnabled: !currentStatus
        });
      fetchPrompts();
    } catch (error) {
      console.error('Error toggling prompt status:', error);
      setError('Failed to toggle prompt status');
    }
  };

  const getPhaseColor = (phase) => {
    const colors = {
      elimination: 'error',
      introduction: 'warning',
      maintenance: 'success'
    };
    return colors[phase] || 'default';
  };

  const getPriorityColor = (priority) => {
    if (priority === 1) return 'error';
    if (priority === 2) return 'warning';
    if (priority === 3) return 'info';
    return 'default';
  };

  const filteredPrompts = prompts.filter(prompt => {
    if (activeTab === 0) return true; // All prompts
    if (activeTab === 1) return prompt.applicablePhases.includes('elimination');
    if (activeTab === 2) return prompt.applicablePhases.includes('introduction');
    if (activeTab === 3) return prompt.applicablePhases.includes('maintenance');
    return true;
  });

  const getTabLabel = (index) => {
    const labels = ['All Phases', 'Elimination', 'Introduction', 'Maintenance'];
    return labels[index];
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            📝 Low FODMAP Phase-Specific Prompts
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage prompts for each Low FODMAP phase with specialized guidance
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add New Prompt
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Prompts
              </Typography>
              <Typography variant="h3">{prompts.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success">
                Active Prompts
              </Typography>
              <Typography variant="h3">{prompts.filter(p => p.isEnabled).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning">
                Introduction Phase
              </Typography>
              <Typography variant="h3">
                {prompts.filter(p => p.applicablePhases.includes('introduction')).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info">
                High Priority
              </Typography>
              <Typography variant="h3">
                {prompts.filter(p => p.priority === 1).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Phase Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="All Phases" />
          <Tab label="Elimination" />
          <Tab label="Introduction" />
          <Tab label="Maintenance" />
        </Tabs>
      </Box>

      {/* Prompts Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Prompt Name</TableCell>
                <TableCell>Applicable Phases</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrompts.map((prompt) => (
                <TableRow key={prompt._id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{prompt.promptName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {prompt.applicablePhases.map((phase) => (
                        <Chip
                          key={phase}
                          label={phase}
                          color={getPhaseColor(phase)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`Priority ${prompt.priority}`}
                      color={getPriorityColor(prompt.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={prompt.isEnabled ? <CheckCircle /> : <Cancel />}
                      label={prompt.isEnabled ? 'Active' : 'Inactive'}
                      color={prompt.isEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                      {prompt.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="info">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(prompt)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={prompt.isEnabled ? 'Deactivate' : 'Activate'}>
                        <IconButton 
                          size="small" 
                          color={prompt.isEnabled ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(prompt._id, prompt.isEnabled)}
                        >
                          {prompt.isEnabled ? <Cancel /> : <CheckCircle />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(prompt._id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt Name"
                value={formData.promptName}
                onChange={(e) => setFormData({ ...formData, promptName: e.target.value })}
                required
                placeholder="e.g., Fructans Testing Week 5-6"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Brief description of what this prompt does"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt Template"
                value={formData.promptTemplate}
                onChange={(e) => setFormData({ ...formData, promptTemplate: e.target.value })}
                required
                placeholder="Enter the prompt template that will be used for meal plan generation"
                multiline
                rows={6}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Applicable Phases</InputLabel>
                <Select
                  multiple
                  value={formData.applicablePhases}
                  onChange={(e) => setFormData({ ...formData, applicablePhases: e.target.value })}
                  label="Applicable Phases"
                >
                  {phases.map((phase) => (
                    <MenuItem key={phase} value={phase}>
                      {phase.charAt(0).toUpperCase() + phase.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      Priority {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Information Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Phase-Specific Prompts:</strong> These prompts are automatically included in meal plan 
          generation based on the user's current Low FODMAP phase. High priority prompts (Priority 1) 
          are included first, followed by lower priority prompts.
        </Typography>
      </Alert>
    </Box>
  );
};

export default LowFodmapPrompts;
